/**
 * TravelOrchestrator — Stagger/cancel/promise queue for collection animations.
 *
 * Drives the "launch item, wait, launch next" loop with configurable stagger,
 * cancellation, and per-item/all-complete callbacks.
 *
 * Game-agnostic: receives items and an animation function, knows nothing about
 * PIXI, spines, or specific game features.
 */
import TickerTimeout from "../../utils/TickerTimeout";

/**
 * Orchestrate a collection of items through an animation function.
 *
 * @param {Array} items - List of items to animate
 * @param {function(item, index): void} animateFn - Called per item; receives (item, index).
 *   Must call the `onComplete` callback (3rd argument) when the item's animation finishes.
 *   Signature: animateFn(item, index, onComplete)
 * @param {object} [options]
 * @param {number} [options.staggerRatio=0.5] - 0 = all at once, 0.5 = overlap, ≥1.0 = sequential
 * @param {number} [options.travelDuration=500] - Duration per item (ms), used for stagger delay calc
 * @param {function(item, index)} [options.onItemComplete] - Called when each item finishes
 * @param {function()} [options.onAllComplete] - Called when all items finish
 * @returns {{ promise: Promise<void>, cancel: function(): void }}
 */
export function orchestrate(items, animateFn, options = {}) {
  const {
    staggerRatio = 0.5,
    travelDuration = 500,
    onItemComplete,
    onAllComplete,
  } = options;

  let cancelled = false;

  const cancel = () => {
    cancelled = true;
  };

  if (!items || items.length === 0) {
    onAllComplete?.();
    return { promise: Promise.resolve(), cancel };
  }

  const promise = new Promise((resolve) => {
    let currentIndex = 0;
    let completedCount = 0;
    const totalItems = items.length;

    const launchNext = () => {
      if (cancelled || currentIndex >= items.length) return;

      const item = items[currentIndex];
      const index = currentIndex;
      currentIndex++;

      animateFn(item, index, () => {
        onItemComplete?.(item, index);

        completedCount++;

        // Sequential mode: launch next only after current completes
        if (staggerRatio >= 1.0 && currentIndex < items.length && !cancelled) {
          launchNext();
        }

        if (completedCount >= totalItems) {
          onAllComplete?.();
          resolve();
        }
      });

      // Non-sequential: stagger via timer (or launch all at once for ratio 0)
      if (staggerRatio < 1.0 && currentIndex < items.length && !cancelled) {
        const staggerDelay = travelDuration * staggerRatio;
        if (staggerDelay > 0) {
          new TickerTimeout(launchNext, staggerDelay);
        } else {
          launchNext(); // Synchronous — all items launch in same frame
        }
      }
    };

    // Launch first item immediately
    launchNext();
  });

  return { promise, cancel };
}
